import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

const DAYS = [
  { key: 'monday',    label: 'Lunes' },
  { key: 'tuesday',   label: 'Martes' },
  { key: 'wednesday', label: 'Miércoles' },
  { key: 'thursday',  label: 'Jueves' },
  { key: 'friday',    label: 'Viernes' },
  { key: 'saturday',  label: 'Sábado' },
  { key: 'sunday',    label: 'Domingo' },
];

const inputSt = {
  padding: '7px 10px',
  background: theme.colors.background,
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '6px',
  color: theme.colors.text,
  fontSize: '0.875rem',
  outline: 'none',
};

const sectionCard = {
  backgroundColor: 'rgba(255,255,255,0.02)',
  border: `1px solid ${theme.colors.border}`,
  borderRadius: '10px',
  padding: '1.25rem',
  marginBottom: '1.5rem',
};

const sectionTitle = {
  color: theme.colors.primary,
  fontSize: '0.95rem',
  fontWeight: '700',
  marginBottom: '1rem',
};

function CampaignEdit({ token }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [openingHours, setOpeningHours] = useState({});
  const [msgOutsideHours, setMsgOutsideHours] = useState('');
  const [msgWithoutAdvisors, setMsgWithoutAdvisors] = useState('');
  const [msgEscalation, setMsgEscalation] = useState('');
  const [currentAgents, setCurrentAgents] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [agentError, setAgentError] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);

  const loadCampaign = useCallback(async () => {
    try {
      const res = await fetch(`/api/campaigns/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setName(data.name);
      setDescription(data.description || '');
      setOpeningHours(data.opening_hours || {});
      setMsgOutsideHours(data.message_outside_of_business_hours || '');
      setMsgWithoutAdvisors(data.message_without_advisors || '');
      setMsgEscalation(data.message_escalation || '');
      setCurrentAgents(data.agents || []);
    } catch {
      setLoadError('No se pudo cargar la campaña');
    } finally {
      setLoading(false);
    }
  }, [id, token]);

  const loadAvailableAgents = useCallback(async () => {
    try {
      const res = await fetch(`/api/campaigns/${id}/available-agents`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setAvailableAgents(data);
      setSelectedUserId(data[0]?.id?.toString() || '');
    } catch { /* silent */ }
  }, [id, token]);

  useEffect(() => {
    loadCampaign();
    loadAvailableAgents();
  }, [loadCampaign, loadAvailableAgents]);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg('');
    try {
      const res = await fetch(`/api/campaigns/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ name, description, opening_hours: openingHours, message_outside_of_business_hours: msgOutsideHours, message_without_advisors: msgWithoutAdvisors, message_escalation: msgEscalation }),
      });
      if (!res.ok) throw new Error();
      setSaveMsg('Guardado correctamente');
      setTimeout(() => setSaveMsg(''), 3000);
    } catch {
      setSaveMsg('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const updateHours = (day, field, value) => {
    setOpeningHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], [field]: value },
    }));
  };

  const handleAddAgent = async () => {
    if (!selectedUserId) return;
    setAgentError('');
    setAgentLoading(true);
    try {
      const res = await fetch(`/api/campaigns/${id}/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ user_id: parseInt(selectedUserId, 10) }),
      });
      const data = await res.json();
      if (!res.ok) { setAgentError(data.message || 'Error al añadir agente'); return; }
      await Promise.all([loadCampaign(), loadAvailableAgents()]);
    } catch {
      setAgentError('Error de conexión');
    } finally {
      setAgentLoading(false);
    }
  };

  const handleRemoveAgent = async (userId) => {
    try {
      await fetch(`/api/campaigns/${id}/agents/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      await Promise.all([loadCampaign(), loadAvailableAgents()]);
    } catch { /* silent */ }
  };

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)', color: theme.colors.textMuted }}>Cargando campaña...</div>;
  if (loadError) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)', color: theme.colors.error }}>{loadError}</div>;

  return (
    <main style={{ padding: isMobile ? '1rem' : '2rem 1.5rem', maxWidth: '860px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.75rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <button onClick={() => navigate('/campaigns')} style={{ background: 'transparent', border: `1px solid ${theme.colors.border}`, color: theme.colors.textMuted, borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '0.85rem' }}>
            ← Volver
          </button>
          <h1 style={{ color: theme.colors.primary, fontSize: '1.4rem', margin: 0 }}>Editar Campaña</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: isMobile ? '100%' : 'auto', justifyContent: isMobile ? 'flex-end' : 'flex-start' }}>
          {saveMsg && <span style={{ fontSize: '0.85rem', color: saveMsg.startsWith('Error') ? theme.colors.error : theme.colors.success }}>{saveMsg}</span>}
          <button onClick={handleSave} disabled={saving} style={{ padding: '8px 24px', backgroundColor: saving ? theme.colors.border : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.9rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
            {saving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      </div>

      {/* Basic info */}
      <div style={{ ...sectionCard, padding: isMobile ? '1rem' : sectionCard.padding }}>
        <div style={sectionTitle}>Información General</div>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '5px' }}>Nombre *</label>
          <input value={name} onChange={(e) => setName(e.target.value)} style={{ ...inputSt, width: '100%', boxSizing: 'border-box' }} />
        </div>
        <div>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '5px' }}>Descripción</label>
          <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} style={{ ...inputSt, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '5px' }}>Mensaje fuera de horario hábil</label>
          <textarea rows={3} value={msgOutsideHours} onChange={(e) => setMsgOutsideHours(e.target.value)} placeholder="Ej: Estamos fuera de horario, te atenderemos mañana..." style={{ ...inputSt, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '5px' }}>Mensaje sin asesores disponibles</label>
          <textarea rows={3} value={msgWithoutAdvisors} onChange={(e) => setMsgWithoutAdvisors(e.target.value)} placeholder="Ej: No hay asesores disponibles en este momento..." style={{ ...inputSt, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
        <div style={{ marginTop: '1rem' }}>
          <label style={{ display: 'block', color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '5px' }}>Mensaje de escalamiento</label>
          <textarea rows={3} value={msgEscalation} onChange={(e) => setMsgEscalation(e.target.value)} placeholder="Ej: ¡Gracias! Un asesor continuará tu atención en un momento..." style={{ ...inputSt, width: '100%', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
        </div>
      </div>

      {/* Opening hours */}
      <div style={{ ...sectionCard, padding: isMobile ? '1rem' : sectionCard.padding }}>
        <div style={sectionTitle}>Horario de Atención</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr>
                {['Día', 'Activo', 'Desde', 'Hasta'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: theme.colors.textMuted, fontWeight: 600, padding: '6px 12px', borderBottom: `1px solid ${theme.colors.border}`, fontSize: '0.8rem' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {DAYS.map(({ key, label }) => {
                const day = openingHours[key] || { enabled: false, from: '08:00', to: '18:00' };
                return (
                  <tr key={key} style={{ borderBottom: `1px solid rgba(255,255,255,0.04)` }}>
                    <td style={{ padding: '8px 12px', color: theme.colors.text, fontWeight: 500 }}>{label}</td>
                    <td style={{ padding: '8px 12px' }}>
                      <input
                        type="checkbox"
                        checked={!!day.enabled}
                        onChange={(e) => updateHours(key, 'enabled', e.target.checked)}
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                      />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <input
                        type="time"
                        value={day.from || '08:00'}
                        disabled={!day.enabled}
                        onChange={(e) => updateHours(key, 'from', e.target.value)}
                        style={{ ...inputSt, opacity: day.enabled ? 1 : 0.4, cursor: day.enabled ? 'text' : 'not-allowed' }}
                      />
                    </td>
                    <td style={{ padding: '8px 12px' }}>
                      <input
                        type="time"
                        value={day.to || '18:00'}
                        disabled={!day.enabled}
                        onChange={(e) => updateHours(key, 'to', e.target.value)}
                        style={{ ...inputSt, opacity: day.enabled ? 1 : 0.4, cursor: day.enabled ? 'text' : 'not-allowed' }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Agents */}
      <div style={{ ...sectionCard, padding: isMobile ? '1rem' : sectionCard.padding }}>
        <div style={sectionTitle}>Agentes Asignados</div>

        {/* Current agents */}
        {currentAgents.length === 0 ? (
          <p style={{ color: theme.colors.textMuted, fontSize: '0.875rem', marginBottom: '1.25rem' }}>Sin agentes asignados aún.</p>
        ) : (
          <div style={{ marginBottom: '1.25rem' }}>
            {currentAgents.map((agent) => (
              <div key={agent.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(56,189,248,0.05)', border: `1px solid ${theme.colors.border}`, borderRadius: '7px', marginBottom: '6px' }}>
                <span style={{ color: theme.colors.text, fontSize: '0.9rem' }}>{agent.username}</span>
                <button
                  onClick={() => handleRemoveAgent(agent.id)}
                  style={{ background: 'transparent', border: '1px solid #ef4444', color: '#ef4444', borderRadius: '5px', padding: '3px 10px', fontSize: '0.78rem', cursor: 'pointer' }}
                >
                  Quitar
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Add agent */}
        <div style={{ borderTop: `1px solid ${theme.colors.border}`, paddingTop: '1rem' }}>
          <div style={{ color: theme.colors.textMuted, fontSize: '0.83rem', marginBottom: '0.6rem', fontWeight: 600 }}>Añadir agente</div>
          {availableAgents.length === 0 ? (
            <p style={{ color: theme.colors.textMuted, fontSize: '0.83rem' }}>No hay agentes disponibles para añadir.</p>
          ) : (
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                style={{ ...inputSt, flex: 1, minWidth: '180px' }}
              >
                {availableAgents.map((u) => (
                  <option key={u.id} value={u.id}>{u.username}</option>
                ))}
              </select>
              <button
                onClick={handleAddAgent}
                disabled={agentLoading || !selectedUserId}
                style={{ padding: '7px 20px', backgroundColor: agentLoading ? theme.colors.border : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.875rem', cursor: agentLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {agentLoading ? 'Añadiendo...' : '+ Añadir'}
              </button>
            </div>
          )}
          {agentError && <p style={{ color: theme.colors.error, fontSize: '0.83rem', marginTop: '0.5rem' }}>{agentError}</p>}
        </div>
      </div>
    </main>
  );
}

export default CampaignEdit;
